import { forwardRef, useMemo } from 'react';
import { StyleSheet, Text, TextInput, View, type TextInputProps } from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type Props = TextInputProps & {
  label?: string;
  error?: string | null;
  rightElement?: React.ReactNode;
};

export const Input = forwardRef<TextInput, Props>(function Input(
  { label, error, style, rightElement, ...rest },
  ref,
) {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  return (
    <View style={styles.container}>
      {!!label && <Text style={styles.label}>{label}</Text>}
      <View style={styles.inputWrap}>
        <TextInput
          ref={ref}
          placeholderTextColor={colors.textMuted}
          style={[styles.input, !!error && styles.inputError, !!rightElement && styles.inputWithRight, style]}
          {...rest}
        />
        {!!rightElement && <View style={styles.rightSlot}>{rightElement}</View>}
      </View>
      {!!error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
});

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: {
      gap: spacing.xs,
    },
    label: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    inputWrap: {
      position: 'relative',
    },
    input: {
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
      color: colors.text,
      fontSize: fontSize.md,
    },
    inputWithRight: {
      paddingRight: 48,
    },
    inputError: {
      borderColor: colors.danger,
    },
    rightSlot: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: 48,
      alignItems: 'center',
      justifyContent: 'center',
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
  });
}
